"use client";

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export default function AboutUsPage() {
  const [isMobile, setIsMobile] = useState(false);
  const [isPinned, setIsPinned] = useState(true);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    const handleScroll = () => {
      // Unpin names once user starts scrolling
      setIsPinned(window.scrollY < 50);
    };

    checkMobile();
    handleScroll();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '0 30px' : '0 40px',
        }}
      >
        <motion.div
          style={{ maxWidth: '1200px', width: '100%', textAlign: 'left' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <h1 style={{
            fontSize: isMobile ? '3rem' : '5.5rem',
            fontWeight: 400,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
            color: '#583b1f',
          }}>
            The world is our design team
          </h1>
        </motion.div>
      </section>

      {/* Team Names Section */}
      <section style={{
        position: isPinned ? 'fixed' : 'relative',
        bottom: isPinned ? 0 : 'auto',
        left: isPinned ? 0 : 'auto',
        right: isPinned ? 0 : 'auto',
        padding: isMobile ? '30px' : '40px',
        backgroundColor: 'transparent',
        zIndex: 10,
        transition: 'opacity 0.3s ease',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: isMobile ? '2rem' : '3rem',
          }}>
            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 400,
                color: '#583b1f',
                marginBottom: '0.25rem',
                letterSpacing: '-0.02em',
              }}>
                Saad Riaz
              </h3>
              <p style={{
                fontSize: '1.125rem',
                color: 'rgba(88, 59, 31, 0.7)',
                fontWeight: 400,
              }}>
                Founder, DFSG
              </p>
            </div>

            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 400,
                color: '#583b1f',
                marginBottom: '0.25rem',
                letterSpacing: '-0.02em',
              }}>
                Daniel Ramirez
              </h3>
              <p style={{
                fontSize: '1.125rem',
                color: 'rgba(88, 59, 31, 0.7)',
                fontWeight: 400,
              }}>
                Software Engineering Lead
              </p>
            </div>

            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 400,
                color: '#583b1f',
                marginBottom: '0.25rem',
                letterSpacing: '-0.02em',
              }}>
                Celena Aponte
              </h3>
              <p style={{
                fontSize: '1.125rem',
                color: 'rgba(88, 59, 31, 0.7)',
                fontWeight: 400,
              }}>
                Advisory Board Member
              </p>
            </div>

            <div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 400,
                color: '#583b1f',
                marginBottom: '0.25rem',
                letterSpacing: '-0.02em',
              }}>
                Patrick Fenton
              </h3>
              <p style={{
                fontSize: '1.125rem',
                color: 'rgba(88, 59, 31, 0.7)',
                fontWeight: 400,
              }}>
                Advisory Board Member
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founding Story Section */}
      <section style={{
        padding: isMobile ? '80px 30px' : '120px 40px',
        background: '#eae6e1',
        minHeight: '100vh',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '2rem' : '2.5rem',
            fontWeight: 400,
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
            color: '#583b1f',
            marginBottom: isMobile ? '3rem' : '4rem',
          }}>
            Founder profile
          </h2>

          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '2rem' : '4rem',
            alignItems: 'flex-start',
          }}>
            {/* Description */}
            <div style={{
              flex: 1,
            }}>
              <h3 style={{
                fontSize: isMobile ? '1.75rem' : '2rem',
                fontWeight: 400,
                color: '#583b1f',
                marginBottom: '1rem',
                letterSpacing: '-0.02em',
              }}>
                Saad Riaz
              </h3>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(88, 59, 31, 0.7)',
                fontWeight: 500,
                marginBottom: '1.5rem',
                letterSpacing: '-0.01em',
              }}>
                Founder, DFSG
              </p>
              <p style={{
                fontSize: isMobile ? '1.125rem' : '1.25rem',
                color: 'rgba(88, 59, 31, 0.85)',
                lineHeight: 1.7,
                fontWeight: 400,
              }}>
                Saad is a Lecturer at Stanford University teaching multiple design courses in the undergraduate design program. He founded DFSG with a vision to bridge design thinking, human-centered research, and emerging AI technologies. With experience spanning consumer good technologies and government and public services, he recognized the need for a new kind of design organization—one that could help shape an AI-native future while keeping human needs at the center.
              </p>
            </div>

            {/* Photo and Links Card */}
            <div style={{
              flex: isMobile ? '1' : '0 0 400px',
              width: isMobile ? '100%' : '400px',
            }}>
              <div style={{
                backgroundColor: 'rgba(212, 165, 116, 0.15)',
                borderRadius: '16px',
                padding: isMobile ? '1.5rem' : '2rem',
                border: '1px solid rgba(212, 165, 116, 0.2)',
              }}>
                <div style={{
                  width: '100%',
                  aspectRatio: '16/9',
                  backgroundColor: 'rgba(212, 165, 116, 0.2)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  marginBottom: '1.5rem',
                }}>
                  <img
                    src="/dschool_SaadHeadShots_2-21-25_PatrickBeaudouin_IMG_3165.jpg"
                    alt="Saad Riaz"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </div>

                {/* Links */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}>
                  <a
                    href="mailto:saad@dfsgdesign.com"
                    style={{
                      fontSize: '1rem',
                      color: '#583b1f',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      transition: 'opacity 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    Email
                  </a>
                  <a
                    href="https://dschool.stanford.edu/directory/saad-riaz"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '1rem',
                      color: '#583b1f',
                      textDecoration: 'underline',
                      fontWeight: 500,
                      transition: 'opacity 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Faculty Profile, d.school
                  </a>
                  <a
                    href="https://www.instagram.com/saadsriaz"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#583b1f',
                      transition: 'opacity 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      textDecoration: 'underline',
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                    @saadsriaz
                  </a>
                  <a
                    href="https://www.linkedin.com/in/saad-r-86677160/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#583b1f',
                      transition: 'opacity 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      textDecoration: 'underline',
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect x="2" y="9" width="4" height="12"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                    LinkedIn profile
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
